/**
 * Provider Type × Care Service affinity helpers (plan 25 P1).
 * Source of truth: `PROVIDER_TYPE_SERVICE_AFFINITY` in generated care taxonomy.
 */

import {
  BUSINESS_TYPE_NORMALIZATION,
  CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY,
  COMMUNITY_SERVICE_DEFINITIONS,
  PROVIDER_TYPE_SERVICE_AFFINITY,
  SERVICE_CANONICAL_TO_LABEL,
  SERVICE_OPTIONS,
  type ProviderTypeServiceAffinity,
} from "../generated/careTaxonomy";
import { mapCareTypeSlugToFilterLabel } from "./providerSearchTaxonomyPayload";
import { serviceDisplayKey } from "./serviceMappings";

export type ServiceAffinityRank = "typical" | "compatible" | "rare";

export const LESS_COMMON_FOR_TYPE_LABEL = "Less common for this type";
/** Narrow sheets — group headings / autocomplete meta. */
export const LESS_COMMON_FOR_TYPE_LABEL_SHORT = "Less common";

const AFFINITY_SORT_RANK: Record<ServiceAffinityRank, number> = {
  typical: 0,
  compatible: 1,
  rare: 2,
};

const COMMUNITY_BY_KEY = new Map<string, (typeof COMMUNITY_SERVICE_DEFINITIONS)[number]>(
  COMMUNITY_SERVICE_DEFINITIONS.map((item) => [item.key, item])
);

/**
 * Resolve Provider Type dropdown label or slug to taxonomy business_type key
 * for service affinity (suggest / rare / invent).
 *
 * Plan 33: never follow ``replaced_by`` here — discovery aliases must not rewrite
 * care-model affinity. Inventory peer expansion stays in BUSINESS_TYPE_SEARCH_PEERS.
 */
export function resolveProviderTypeAffinityKey(
  businessType: string | undefined
): string | undefined {
  const trimmed = businessType?.trim();
  if (!trimmed) return undefined;
  if (PROVIDER_TYPE_SERVICE_AFFINITY[trimmed]) return trimmed;

  // Stored aliases (e.g. behavioral_health → behavioral_health_provider).
  const normalized = BUSINESS_TYPE_NORMALIZATION[trimmed.toLowerCase()];
  if (normalized && PROVIDER_TYPE_SERVICE_AFFINITY[normalized]) {
    return normalized;
  }

  const fromDisplay = CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY[trimmed];
  if (fromDisplay && PROVIDER_TYPE_SERVICE_AFFINITY[fromDisplay]) {
    return fromDisplay;
  }

  const fromSlug = mapCareTypeSlugToFilterLabel(trimmed);
  if (fromSlug) {
    const key = CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY[fromSlug];
    if (key && PROVIDER_TYPE_SERVICE_AFFINITY[key]) {
      return key;
    }
  }
  return undefined;
}

export function getProviderTypeAffinity(
  businessType: string | undefined
): ProviderTypeServiceAffinity | undefined {
  const key = resolveProviderTypeAffinityKey(businessType);
  return key ? PROVIDER_TYPE_SERVICE_AFFINITY[key] : undefined;
}

/**
 * Chip-friendly suggest label for a canonical service name.
 * Prefers a human-readable short alias (e.g. "Personal Care", "Nursing Care")
 * over long panel labels. Never surfaces raw abbreviations or snake_case aliases
 * (e.g. "pt", "ot", "st", "physical_therapy") — those remain match-only aliases.
 */
export function suggestLabelForCanonical(canonical: string): string {
  const option = SERVICE_OPTIONS.find((svc) => svc.canonical === canonical);
  if (option) {
    const aliases = option.aliases as readonly string[];
    const shortAlias = aliases.find(
      (alias) =>
        !alias.includes("_") &&
        alias.length < option.label.length &&
        isHumanReadableSuggestAlias(alias)
    );
    if (shortAlias) return shortAlias;
    return option.label;
  }
  return SERVICE_CANONICAL_TO_LABEL[canonical] ?? canonical;
}

/** Title-ish / multi-word aliases only — not machine abbreviations. */
function isHumanReadableSuggestAlias(alias: string): boolean {
  const trimmed = alias.trim();
  if (!trimmed) return false;
  // Reject snake-free but opaque abbreviations: "pt", "ot", "st", "rn"
  if (/^[a-z]{1,4}$/.test(trimmed)) return false;
  // Prefer phrases with a capital letter or multiple words
  return /[A-Z]/.test(trimmed) || /\s/.test(trimmed);
}

export function communityServiceDisplayLabel(key: string): string | undefined {
  return COMMUNITY_BY_KEY.get(key)?.label;
}

/** Typical general service suggest labels for a Provider Type (plan 25 P1.5). */
export function getTypicalGeneralSuggestLabels(
  businessType: string | undefined
): string[] {
  const affinity = getProviderTypeAffinity(businessType);
  if (!affinity?.typical_general?.length) return [];
  return affinity.typical_general.map(suggestLabelForCanonical);
}

/** Typical community service keys + labels for a Provider Type. */
export function getTypicalCommunitySuggestEntries(
  businessType: string | undefined
): { key: string; label: string }[] {
  const affinity = getProviderTypeAffinity(businessType);
  if (!affinity?.typical_community_keys?.length) return [];
  const entries: { key: string; label: string }[] = [];
  for (const key of affinity.typical_community_keys) {
    const label = communityServiceDisplayLabel(key);
    if (label) entries.push({ key, label });
  }
  return entries;
}

/**
 * True when the type is community-led (typical community keys, no general
 * typicals) — BH / HCBS / case management. Cards and suggest strips should not
 * invent Care Coordination / Companionship general chips for these.
 */
export function isCommunityLedProviderType(
  businessType: string | undefined
): boolean {
  const affinity = getProviderTypeAffinity(businessType);
  if (!affinity?.typical_community_keys?.length) return false;
  return !(affinity.typical_general?.length ?? 0);
}

function canonicalSet(values: readonly string[] | undefined): Set<string> {
  return new Set(values ?? []);
}

/**
 * Affinity for a catalog option. Unset pairs → compatible (fail open).
 */
export function resolveCatalogOptionAffinity(
  businessType: string | undefined,
  kind: "community" | "general" | "condition" | "feature",
  value: string
): ServiceAffinityRank {
  if (kind === "condition" || kind === "feature") {
    return "compatible";
  }
  const affinity = getProviderTypeAffinity(businessType);
  if (!affinity) return "compatible";

  if (kind === "general") {
    const canonical =
      SERVICE_OPTIONS.find((svc) => {
        const aliases = svc.aliases as readonly string[];
        return (
          svc.canonical === value ||
          svc.label === value ||
          aliases.includes(value) ||
          serviceDisplayKey(svc.label) === serviceDisplayKey(value)
        );
      })?.canonical ?? value;
    if (canonicalSet(affinity.typical_general).has(canonical)) return "typical";
    if (canonicalSet(affinity.rare_general).has(canonical)) return "rare";
    if (canonicalSet(affinity.compatible_general).has(canonical)) {
      return "compatible";
    }
    return "compatible";
  }

  if (canonicalSet(affinity.typical_community_keys).has(value)) return "typical";
  if (canonicalSet(affinity.rare_community_keys).has(value)) return "rare";
  if (canonicalSet(affinity.compatible_community_keys).has(value)) {
    return "compatible";
  }
  return "compatible";
}

export function affinitySortRank(rank: ServiceAffinityRank): number {
  return AFFINITY_SORT_RANK[rank];
}
